/**
 * The built-in developmental framework.
 *
 * Four authored modules, one assembled taxonomy. Each module owns a few
 * domains; this barrel normalizes their exports, orders everything, and
 * validates the skill graph so a broken prerequisite can never ship.
 */

import type { DomainDef, DomainKey, Framework, Skill, SkillId } from '../types'
import { DOMAIN_KEYS } from '../types'

import {
  LITERACY_DOMAIN, LANGUAGE_DOMAIN, LOGIC_DOMAIN,
  LITERACY_SKILLS, LANGUAGE_SKILLS, LOGIC_SKILLS,
} from './literacy-language-logic'

import { mathMotorDomains, mathMotorSkills } from './math-motor'

import {
  SOCIAL_EXECUTIVE_CREATIVE_DOMAINS,
  SOCIAL_EXECUTIVE_CREATIVE_SKILLS,
} from './social-executive-creative'

import {
  TIME_SCIENCE_LANGUAGE_SELFCARE_DOMAINS,
  TIME_SCIENCE_LANGUAGE_SELFCARE_SKILLS,
} from './time-science-language-selfcare'

export const FRAMEWORK_KEY = 'aurora-core'
export const FRAMEWORK_VERSION = '1.0.0'

/** Presentation order. Priority domains first, then supporting ones. */
const DOMAIN_ORDER: DomainKey[] = [
  'literacy', 'logic', 'language', 'math',
  'fineMotor', 'creative', 'grossMotor',
  'socialEmotional', 'executiveFunction',
  'time', 'science', 'secondLanguage', 'selfCare',
]

const RAW_DOMAINS: DomainDef[] = [
  LITERACY_DOMAIN, LANGUAGE_DOMAIN, LOGIC_DOMAIN,
  ...mathMotorDomains,
  ...SOCIAL_EXECUTIVE_CREATIVE_DOMAINS,
  ...TIME_SCIENCE_LANGUAGE_SELFCARE_DOMAINS,
]

const RAW_SKILLS: Skill[] = [
  ...LITERACY_SKILLS, ...LANGUAGE_SKILLS, ...LOGIC_SKILLS,
  ...mathMotorSkills,
  ...SOCIAL_EXECUTIVE_CREATIVE_SKILLS,
  ...TIME_SCIENCE_LANGUAGE_SELFCARE_SKILLS,
]

export const DOMAINS: DomainDef[] = [...RAW_DOMAINS]
  .sort((a, b) => DOMAIN_ORDER.indexOf(a.key) - DOMAIN_ORDER.indexOf(b.key))
  .map((d, i) => ({ ...d, sortOrder: i }))

export const SKILLS: Skill[] = [...RAW_SKILLS].sort((a, b) => {
  const d = DOMAIN_ORDER.indexOf(a.domainKey) - DOMAIN_ORDER.indexOf(b.domainKey)
  return d !== 0 ? d : a.sortOrder - b.sortOrder
})

export const BUILT_IN_FRAMEWORK: Omit<Framework, '_id' | 'createdAt' | 'updatedAt'> = {
  name: 'Aurora Core',
  version: FRAMEWORK_VERSION,
  source:
    'Synthesized from Piaget, Vygotsky, Lowenfeld (drawing stages), ' +
    'Gentry (spelling stages), Ehri (reading phases), and Clay (early literacy).',
  isBuiltIn: true,
}

// ── lookups ──────────────────────────────────────────────────

const skillById = new Map<string, Skill>(SKILLS.map((s) => [s._id, s]))
const skillsByDomain = new Map<DomainKey, Skill[]>()
for (const s of SKILLS) {
  const list = skillsByDomain.get(s.domainKey) ?? []
  list.push(s)
  skillsByDomain.set(s.domainKey, list)
}
const domainByKey = new Map<DomainKey, DomainDef>(DOMAINS.map((d) => [d.key, d]))

export function getSkill(id: SkillId | string): Skill | undefined {
  return skillById.get(id as string)
}

export function getSkillsForDomain(key: DomainKey): Skill[] {
  return skillsByDomain.get(key) ?? []
}

export function getDomain(key: DomainKey): DomainDef | undefined {
  return domainByKey.get(key)
}

// ── validation ───────────────────────────────────────────────

export interface FrameworkValidation {
  ok: boolean
  errors: string[]
  stats: { domains: number; skills: number; byDomain: Record<string, number> }
}

/**
 * Structural invariants. Run in a test so a malformed taxonomy fails the build
 * rather than surfacing as a confusing empty screen months later.
 */
export function validateFramework(): FrameworkValidation {
  const errors: string[] = []

  const ids = new Set<string>()
  for (const s of SKILLS) {
    if (ids.has(s._id)) errors.push(`duplicate skill id: ${s._id}`)
    ids.add(s._id)
  }

  for (const key of DOMAIN_KEYS) {
    if (!domainByKey.has(key)) errors.push(`missing domain definition: ${key}`)
    if (getSkillsForDomain(key).length === 0) errors.push(`domain has no skills: ${key}`)
  }

  for (const s of SKILLS) {
    if (s.levelDescriptors.length !== 5) {
      errors.push(`${s._id}: expected 5 level descriptors, got ${s.levelDescriptors.length}`)
    }
    const levels = s.levelDescriptors.map((d) => d.level).sort((a, b) => a - b)
    if (levels.join(',') !== '1,2,3,4,5') {
      errors.push(`${s._id}: level descriptors must cover levels 1-5 exactly`)
    }
    for (const d of s.levelDescriptors) {
      if (!d.descriptor?.trim()) errors.push(`${s._id} L${d.level}: empty descriptor`)
    }
    if (s.assessmentHints.length === 0) errors.push(`${s._id}: no assessment hints`)

    for (const prereq of s.prerequisites) {
      if (!skillById.has(prereq as string)) {
        errors.push(`${s._id}: prerequisite not found: ${prereq}`)
      }
      if ((prereq as string) === s._id) errors.push(`${s._id}: skill is its own prerequisite`)
    }
  }

  // A cycle in the prerequisite graph would make readiness unresolvable.
  const state = new Map<string, 0 | 1 | 2>()
  const walk = (id: string, trail: string[]): void => {
    const seen = state.get(id)
    if (seen === 2) return
    if (seen === 1) {
      errors.push(`prerequisite cycle: ${[...trail, id].join(' -> ')}`)
      return
    }
    state.set(id, 1)
    for (const p of skillById.get(id)?.prerequisites ?? []) {
      walk(p as string, [...trail, id])
    }
    state.set(id, 2)
  }
  for (const s of SKILLS) walk(s._id, [])

  const byDomain: Record<string, number> = {}
  for (const key of DOMAIN_KEYS) byDomain[key] = getSkillsForDomain(key).length

  return {
    ok: errors.length === 0,
    errors,
    stats: { domains: DOMAINS.length, skills: SKILLS.length, byDomain },
  }
}
